import { createFileRoute, Link } from '@tanstack/react-router';
import { DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE, PageLayout } from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { ActionTileGrid, type ActionTileGridTile } from '@interxfi/design-system/components/action-tile/grid/action-tile-grid';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Box } from '@interxfi/design-system/components/box/box';
import { ButtonLink } from '@interxfi/design-system/components/button';
import type { TypographyProps } from '@interxfi/design-system/components/typography';
import { BaseTypography, Typography } from '@interxfi/design-system/components/typography';
import { TypographyProvider } from '@interxfi/design-system/components/typography/context';
import { DecorativeGuidesIcon } from '@interxfi/design-system/icons/decorative/guides/decorative-guides-icon';
import AvatarIcon from '@interxfi/design-system/icons/react/icon-avatar';
import BankIcon from '@interxfi/design-system/icons/react/icon-bank';
import CalendarIcon from '@interxfi/design-system/icons/react/icon-calendar';
import DocIcon from '@interxfi/design-system/icons/react/icon-doc';
import OrdersIcon from '@interxfi/design-system/icons/react/icon-orders';
import PortfolioIcon from '@interxfi/design-system/icons/react/icon-portfolio';
import type { ResponsiveBoxSizing, ResponsiveValue, SpaceUnit } from '@interxfi/design-system/util/responsive';
import { DevelopersSection } from '../../../features/docs/developers-section';
import { DOCS_LEVEL_2_HEADING_TYPOGRAPHY, DocsHeading } from '../../../features/docs/docs-heading';
import { Prose } from '../../../features/docs/prose/prose';
import { buildPathForDocsItem } from '../../../features/docs/utils';
import gridContainerStyles from '../../../features/docs/grid-container/grid-container.module.scss';

const guidesSectionGridColumns = {
  sm: 1,
  md: 3,
} as const satisfies ResponsiveValue<number>;

const baseTypographyProps = {
  lineHeight: '1.5rem',
  size: '1rem',
  weight: 'regular',
} as const satisfies TypographyProps;

const introPadding = {
  left: '0px',
  right: '0px',
  top: '32px',
  bottom: '32px',
} as const satisfies ResponsiveBoxSizing<SpaceUnit>;

const guidesPadding = {
  left: '0px',
  right: '0px',
  top: '0',
  bottom: '32px',
} as const satisfies ResponsiveBoxSizing<SpaceUnit>;

// TODO: get from registry at some point?
const guidesSectionGridItems: ActionTileGridTile[] = [
  {
    title: 'Registration',
    illustration: <AvatarIcon aria-hidden />,
    tone: 'primary',
    subtitle: 'Customer profiles and brokerage accounts',
    interaction: {
      type: 'link',
      link: {
        LinkElement: Link,
        to: buildPathForDocsItem('/docs/guide/registration'),
      },
    },
  },
  {
    title: 'Funding',
    illustration: <BankIcon aria-hidden />,
    tone: 'secondary',
    subtitle: 'External funding sources and transfers',
    interaction: {
      type: 'link',
      link: {
        LinkElement: Link,
        to: buildPathForDocsItem('/docs/guide/funding'),
      },
    },
  },
  {
    title: 'Orders',
    illustration: <OrdersIcon aria-hidden />,
    tone: 'tertiary',
    subtitle: 'Equity and money market funds trading',
    interaction: {
      type: 'link',
      link: {
        LinkElement: Link,
        to: buildPathForDocsItem('/docs/guide/orders'),
      },
    },
  },
  {
    title: 'Portfolio',
    illustration: <PortfolioIcon aria-hidden />,
    tone: 'quaternary',
    subtitle: 'Balances, Transactions, Holds, and Funding',
    interaction: {
      type: 'link',
      link: {
        LinkElement: Link,
        to: buildPathForDocsItem('/docs/guide/portfolio'),
      },
    },
  },
  {
    title: 'Documents',
    illustration: <DocIcon aria-hidden />,
    tone: 'danger',
    subtitle: 'Customer-facing account documents',
    interaction: {
      type: 'link',
      link: {
        LinkElement: Link,
        to: buildPathForDocsItem('/docs/guide/documents'),
      },
    },
  },
  {
    title: 'Hours',
    illustration: <CalendarIcon aria-hidden />,
    tone: 'green',
    subtitle: 'Market trading hours and exceptions',
    interaction: {
      type: 'link',
      link: {
        LinkElement: Link,
        to: buildPathForDocsItem('/docs/guide/hours'),
      },
    },
  },
];

const paragraphStyles = {
  margin: 0,
};

// Empty stable ref to prevent default styling from kicking in
const tileStyle = {};

export const Route = createFileRoute('/docs/_layout/')({
  component: DocsIndex,
});

function DocsIndex() {
  return (
    <PageLayout
      gap={7}
      maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}
      padding={{
        bottom: '0',
        left: { sm: '16px', md: '32px', xl: '128px' },
        right: { sm: '16px', md: '32px', xl: '128px' },
        top: { sm: '16px', md: '32px' },
      }}
    >
      <PageLayout.Section noBorder>
        <Box padding={introPadding}>
          <VerticalStack as="section" gap={7}>
            <DocsHeading {...DOCS_LEVEL_2_HEADING_TYPOGRAPHY}>Documentation</DocsHeading>

            <Prose>
              <TypographyProvider value={baseTypographyProps}>
                <Typography style={paragraphStyles} weight="medium">
                  <VerticalStack gap={4}>
                    <Typography weight="medium">
                      Interchange is a developer platform purpose-built for creating securities-based financial products—from the ground up, with
                      precision and care.
                    </Typography>

                    <BaseTypography style={paragraphStyles} tone="neutral-dk" weight="regular">
                      Our APIs provide low-level, direct connectivity to the U.S. financial markets, enabling you to deliver a seamless,
                      high-performance investing experience—fully branded and uniquely yours.
                      <br />
                      <br />
                      We're on a mission to move the industry beyond slow, batch-based legacy systems, toward a modern, real-time architecture powered
                      by today's best technologies.
                      <br />
                      <br />
                      For technical assistance or feedback, reach out to us at <a href="mailto:support@interchange.com">support@interchange.com</a>.
                      For more immediate integration support, we're happy to set up a shared Slack channel.
                    </BaseTypography>
                  </VerticalStack>
                </Typography>
              </TypographyProvider>
            </Prose>
          </VerticalStack>
        </Box>
      </PageLayout.Section>

      <PageLayout.Section noBorder>
        <Box padding={guidesPadding}>
          <VerticalStack as="section" gap={7}>
            <Typography lineHeight="1.625rem" size="1.125rem" variant="heading" weight="bold">
              Guides
            </Typography>

            <VerticalStack gap={4}>
              <ActionTileGrid columns={guidesSectionGridColumns} defaultStyle={tileStyle} items={guidesSectionGridItems} LinkElement={Link} />
              <ButtonLink
                affixIcon={<DecorativeGuidesIcon aria-hidden />}
                fullWidth
                LinkElement={Link}
                size="large"
                to={buildPathForDocsItem('/docs/guide')}
                variant="light"
              >
                View All Guides
              </ButtonLink>
            </VerticalStack>
          </VerticalStack>
        </Box>
      </PageLayout.Section>

      <PageLayout.Section className={gridContainerStyles.gridContainer} noBorder>
        <DevelopersSection />
      </PageLayout.Section>
    </PageLayout>
  );
}
